import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Switch>. Markup, the
// visually-hidden native checkbox (role="switch"), and CSS ported
// letter-for-letter from the vendor's compiled bundle and its Switch CSS
// — new .rc-switch class names. One vendor branch NOT ported: the
// `:root[data-ds-mode="hc"]` high-contrast override, unreachable in this
// app for the same reason documented in Skeleton.tsx.
export type SwitchSize = "sm" | "md";

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "onChange"> {
  label: ReactNode;
  description?: ReactNode;
  size?: SwitchSize;
  labelPosition?: "before" | "after";
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, description, size = "md", labelPosition = "after", disabled, className, id: providedId, onChange, ...rest },
  ref
) {
  const fallbackId = useId();
  const inputId = providedId ?? fallbackId;
  const descId = description ? `${inputId}-desc` : undefined;

  return (
    <label
      htmlFor={inputId}
      className={cn("rc-switch", `rc-switch--${size}`, labelPosition === "before" && "rc-switch--reverse", disabled && "rc-switch--disabled", className)}
    >
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        role="switch"
        className="rc-switch__input"
        disabled={disabled}
        aria-describedby={descId}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...rest}
      />
      <span className="rc-switch__track" aria-hidden="true">
        <span className="rc-switch__thumb" />
      </span>
      <span className="rc-switch__text">
        <span className="rc-switch__label">{label}</span>
        {description ? (
          <span id={descId} className="rc-switch__description">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
});
